import React, { FC, useContext, useEffect, useMemo, useRef } from 'react';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { useTranslation } from 'react-i18next';
import { usePortfolioBalances } from 'views/Portfolio/components/Summary/hooks/usePortfolioBalances';
import { useQuery } from '@tanstack/react-query';
import { UserProfileResponse, fetchProfile } from 'services';
import { ChangeRequestIcon } from '../../../assets/icons';
import { DisplayField, DisplayFieldType, DisplaySection } from '../../../components/DisplayForms';
import DisplayForm from '../../../components/DisplayForms/DisplayForm';
import { ProfileSkeleton } from '../../../components/Skeleton';
import { AppContext } from '../../../context/ContextProvider';
import { getFieldAttributes } from '../../../helpers';
import { PortfolioBalance, UserProfile } from '../../../types/DomainTypes';
import { buildDisplayText, toInternalId } from '../../../utils';
import PortfolioMetaDataTable from './PortfolioMetaDataTable';

enum DisplayTextKeys {
  PAGE_TITLE = 'page-title',
  BILLING_ADDRESS_TEXT = 'billing_address_text',
  CHANGE_REQUEST = 'change-request',
  FIRST_NAME = 'firstname',
  LAST_NAME = 'lastname',
  DOB = 'dob',
  EMAIL = 'email',
  COUNTRY = 'country',
  CITY = 'city',
  ADDRESS_1 = 'address_1',
  ADDRESS_2 = 'address_2',
  ADDRESS_3 = 'address_3',
  POSTCODE = 'postcode',
  STATE = 'state',
}

enum ModelKeys {
  FIRST_NAME = 'firstname',
  LAST_NAME = 'lastname',
  DOB = 'dob',
  EMAIL = 'email',
  ADDRESS_1 = 'address_1',
  ADDRESS_2 = 'address_2',
  ADDRESS_3 = 'address_3',
  POSTCODE = 'postcode',
  CITY = 'city',
  STATE = 'state',
  COUNTRY = 'country',
}
type ModelType = { [key: string]: string };

const buildUserProfileModel = (userProfile: UserProfileResponse): ModelType => {
  const userProfileModelMapper = new Map<keyof UserProfile, ModelKeys>([
    ['firstName', ModelKeys.FIRST_NAME],
    ['lastName', ModelKeys.LAST_NAME],
    ['email', ModelKeys.EMAIL],
    ['dateOfBirth', ModelKeys.DOB],
    ['addressline1', ModelKeys.ADDRESS_1],
    ['addressline2', ModelKeys.ADDRESS_2],
    ['addressline3', ModelKeys.ADDRESS_3],
    ['city', ModelKeys.CITY],
    ['country', ModelKeys.COUNTRY],
    ['postCode', ModelKeys.POSTCODE],
    ['state', ModelKeys.STATE],
  ]);

  return Object.keys(userProfile).reduce((results, key) => {
    return {
      ...results,
      [userProfileModelMapper.get(key as keyof UserProfile) as keyof UserProfile]:
        userProfile[key as keyof UserProfile],
    };
  }, {});
};

const defaultModel: ModelType = {
  [ModelKeys.FIRST_NAME]: '',
  [ModelKeys.LAST_NAME]: '',
  [ModelKeys.DOB]: '',
  [ModelKeys.EMAIL]: '',
  [ModelKeys.COUNTRY]: '',
  [ModelKeys.CITY]: '',
  [ModelKeys.ADDRESS_1]: '',
  [ModelKeys.ADDRESS_2]: '',
  [ModelKeys.ADDRESS_3]: '',
  [ModelKeys.POSTCODE]: '',
  [ModelKeys.STATE]: '',
};

function filterPortfolios(portfolio: PortfolioBalance): boolean {
  if (toInternalId(portfolio.portfolioName) === 'allportfolios' || portfolio?.feeModel?.toLowerCase() !== 'upfront') {
    return false;
  }

  return true;
}

interface ProfileProps {
  onChangeRequest: (e: React.MouseEvent<HTMLDivElement, MouseEvent>) => void;
}
const Profile: FC<ProfileProps> = ({ onChangeRequest }) => {
  const { anniversaryDate14788 } = useFlags();

  const { t } = useTranslation();
  const {
    state: {
      settings: { email },
    },
  } = useContext(AppContext);

  const {
    error: failedToLoadPortfolioBalances,
    loading: loadingPortfolioBalances,
    portfolioBalances,
  } = usePortfolioBalances();

  const filteredPortfolios = anniversaryDate14788 ? portfolioBalances.filter(filterPortfolios) : [];

  const displayText = useMemo(() => buildDisplayText(Object.values(DisplayTextKeys), 'account:profile', t), [t]);
  const skipQuery = !email;

  const {
    isLoading: loadingQuery,
    error: userProfileError,
    data: userData,
  } = useQuery({
    queryKey: ['HubSpotUserProfile', email],
    queryFn: () => fetchProfile({ email }),
    enabled: !!email,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
  });

  const loading = loadingQuery || skipQuery;

  const model = useMemo(() => {
    return { ...defaultModel, ...(userProfileError || !userData ? {} : buildUserProfileModel(userData)) };
  }, [userData, userProfileError]);

  const containerRef = useRef<HTMLDivElement | null>();
  const containerClassName = 'w-full items-center flex sm:flex-row flex-col';

  const personalInfoSections: DisplaySection[] = useMemo(() => {
    const personalInfoFields: DisplayField[] = (['LAST_NAME', 'FIRST_NAME', 'DOB', 'EMAIL'] as const).map((field) => {
      return {
        ...getFieldAttributes(field, ModelKeys[`${field}` as const], displayText[DisplayTextKeys[`${field}` as const]]),
        disabled: true,
      };
    });

    const name = model[ModelKeys.FIRST_NAME];
    const lastname = model[ModelKeys.LAST_NAME];
    const userInitials = name && lastname ? `${name.charAt(0)}${lastname.charAt(0)}`.toUpperCase() : '';

    return [
      {
        className: 'w-full items-center flex mt-5',
        fields: [
          {
            containerClassName: 'w-[50px] mr-5',
            id: 'initials',
            name: 'initials',
            type: DisplayFieldType.CUSTOM,
            customTemplate: () => {
              return (
                <div className="rounded-full flex items-center justify-center bg-gradient-to-br from-orange to-orange/80 dark:from-teal-500 dark:to-teal-600 w-[50px] h-[50px] text-white font-semibold shadow-lg shadow-orange/25 dark:shadow-teal-500/25">
                  {userInitials}
                </div>
              );
            },
          },
          ...personalInfoFields
            .filter((x) => ['LAST_NAME'].includes(x.id))
            .map((x) => ({ ...x, className: 'flex-1', containerClassName: 'mt-0' })),
        ],
      },
      {
        className: containerClassName,
        fields: personalInfoFields.filter((x) => ['FIRST_NAME', 'DOB'].includes(x.id)),
      },
      {
        className: containerClassName,
        fields: personalInfoFields.filter((x) => ['EMAIL'].includes(x.id)),
      },
    ];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [model]);

  const addressSections: DisplaySection[] = useMemo(() => {
    const addressFields: DisplayField[] = (
      ['ADDRESS_1', 'ADDRESS_2', 'ADDRESS_3', 'CITY', 'STATE', 'COUNTRY', 'POSTCODE'] as const
    ).map((field) => {
      return {
        ...getFieldAttributes(field, ModelKeys[`${field}` as const], displayText[DisplayTextKeys[`${field}` as const]]),
        disabled: true,
      };
    });

    return [
      {
        className: ' w-full ',
        fields: addressFields.filter((x) => x.id.startsWith('ADDRESS')),
      },
      {
        className: ' w-full   flex sm:flex-row  flex-col',
        fields: addressFields.filter((x) => ['CITY', 'STATE'].includes(x.id)),
      },
      {
        className: '  w-full  flex sm:flex-row  flex-col',
        fields: addressFields.filter((x) => ['COUNTRY', 'POSTCODE'].includes(x.id)),
      },
    ];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [model]);

  useEffect(() => {
    if (containerRef.current) containerRef.current.scrollTo(0, 0);
  }, []);
  return (
    <div
      className="flex flex-col w-full h-full py-5 px-8 sm:px-10 lg:px-12 overflow-x-hidden bg-gray-50 dark:bg-slate-900"
      ref={(r) => {
        containerRef.current = r;
      }}
    >
      <div className="flex items-center w-full mb-6">
        <div className="flex-1 text-lg font-semibold text-gray-900 dark:text-white">
          {displayText[DisplayTextKeys.PAGE_TITLE]}
        </div>
        <div
          className="flex items-center pr-5 text-sm cursor-pointer text-orange dark:text-teal-400 hover:text-orange/80 dark:hover:text-teal-300 transition-colors"
          onClick={onChangeRequest}
        >
          <ChangeRequestIcon className="mr-2 w-4 h-4" />
          <span className="underline font-medium">{displayText[DisplayTextKeys.CHANGE_REQUEST]}</span>
        </div>
      </div>

      {loading ? (
        <div className="w-full h-full p-6">
          <ProfileSkeleton />
        </div>
      ) : (
        <>
          <DisplayForm sections={personalInfoSections} model={{ ...model, modelType: 'Personal' }} />
          {/* <div className="w-full border-b border-gray-300 mt-4 mb-4" /> */}
          {/* FIXME: Should not really be a h1 but the title inside the DisplayForm (see component rendered above and below this) are h1s, so I need to adhere to that */}
          {anniversaryDate14788 &&
            !loadingPortfolioBalances &&
            !failedToLoadPortfolioBalances &&
            filteredPortfolios.length > 0 && (
              <>
                <h1 className="mt-8 mb-4 text-lg font-semibold text-gray-900 dark:text-white">
                  {t('account:portfolioDetails')}
                </h1>
                <PortfolioMetaDataTable portfolios={filteredPortfolios} />
              </>
            )}
          <DisplayForm
            titleContainerClassName="mt-10"
            sectionsContainerClassName="pb-10"
            sections={addressSections}
            title={displayText[DisplayTextKeys.BILLING_ADDRESS_TEXT]}
            model={{ ...model, modelType: displayText[DisplayTextKeys.BILLING_ADDRESS_TEXT] }}
          />
        </>
      )}
    </div>
  );
};

export default Profile;
